import { useMemo } from "react";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Icon from "@mui/material/Icon";
import { useICCCColors } from "./theme";

const stateColorMap = (ICCC_COLORS) => ({
  ok: ICCC_COLORS.healthy,
  active: ICCC_COLORS.accent,
  warning: ICCC_COLORS.warning,
  critical: ICCC_COLORS.critical,
  idle: ICCC_COLORS.unknown,
});

const buildStages = (rollup) => {
  const total = rollup?.total || 0;
  const critical = rollup?.critical || 0;
  const warning = rollup?.warning || 0;
  const criticalPct = total ? critical / total : 0;
  const degraded = warning + critical;

  return [
    {
      id: 1,
      title: "Devices Health",
      subtitle: "Data Collected",
      icon: "monitor_heart",
      state: total > 0 ? "ok" : "idle",
    },
    {
      id: 2,
      title: "Device Health",
      subtitle: "Degradation Analysis",
      icon: "analytics",
      state: "active",
    },
    {
      id: 3,
      title: "Ad-hoc Patch",
      subtitle: "Applied",
      icon: "build_circle",
      state: "ok",
    },
    {
      id: 4,
      title: "Critical Component",
      subtitle: "Obsolescence Found",
      icon: "report_problem",
      state: degraded > 0 ? "warning" : "ok",
    },
    {
      id: 5,
      title: "Full System",
      subtitle: "Modernization Needed",
      icon: "settings_suggest",
      state: criticalPct >= 0.25 ? "critical" : critical > 0 ? "warning" : "ok",
    },
  ];
};

const Stage = ({ stage, colors }) => {
  const ICCC_COLORS = colors;
  const STATE_COLOR = stateColorMap(ICCC_COLORS);
  const color = STATE_COLOR[stage.state] || ICCC_COLORS.unknown;
  return (
    <MDBox
      role="listitem"
      aria-label={`${stage.title} — ${stage.subtitle} (${stage.state})`}
      sx={{
        flex: "1 1 0",
        minWidth: 150,
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 1.5,
        py: 1.25,
        borderRadius: 1.5,
        bgcolor: ICCC_COLORS.cardBg2,
        border: `1px solid ${color}55`,
        borderLeft: `4px solid ${color}`,
      }}
    >
      <MDBox
        sx={{
          width: 38,
          height: 38,
          borderRadius: "50%",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}22`,
          color,
          ...(stage.state === "active" && {
            animation: "icccPulse 1.6s ease-in-out infinite",
            "@keyframes icccPulse": {
              "0%, 100%": { boxShadow: `0 0 0 0 ${color}55` },
              "50%": { boxShadow: `0 0 0 6px ${color}00` },
            },
          }),
        }}
      >
        <Icon sx={{ fontSize: 22 }}>{stage.icon}</Icon>
      </MDBox>
      <MDBox sx={{ minWidth: 0 }}>
        <MDTypography
          variant="caption"
          fontWeight="bold"
          sx={{ color: ICCC_COLORS.textPrimary, display: "block", lineHeight: 1.2, fontSize: 12 }}
        >
          {stage.title}
        </MDTypography>
        <MDTypography
          variant="caption"
          sx={{ color: ICCC_COLORS.textMuted, display: "block", lineHeight: 1.2, fontSize: 11 }}
        >
          {stage.subtitle}
        </MDTypography>
      </MDBox>
    </MDBox>
  );
};

Stage.propTypes = {
  stage: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    icon: PropTypes.string,
    state: PropTypes.string,
  }).isRequired,
  colors: PropTypes.object,
};

export default function InformationFlow({ rollup }) {
  const ICCC_COLORS = useICCCColors();
  const stages = useMemo(() => buildStages(rollup), [rollup]);

  return (
    <MDBox
      sx={{
        bgcolor: ICCC_COLORS.cardBg,
        border: `1px solid ${ICCC_COLORS.cardBorder}`,
        borderRadius: 1.5,
        p: 2,
        boxShadow: ICCC_COLORS.shadow,
      }}
    >
      <MDBox display="flex" alignItems="center" gap={1} mb={1.5}>
        <Icon sx={{ color: ICCC_COLORS.accent }}>account_tree</Icon>
        <MDTypography variant="h6" fontWeight="bold" sx={{ color: ICCC_COLORS.textPrimary }}>
          Information Flow
        </MDTypography>
      </MDBox>

      <MDBox
        role="list"
        sx={{
          display: "flex",
          alignItems: "stretch",
          gap: 1,
          flexWrap: { xs: "wrap", lg: "nowrap" },
        }}
      >
        {stages.map((stage, i) => (
          <MDBox
            key={stage.id}
            sx={{ display: "flex", alignItems: "center", gap: 1, flex: "1 1 0", minWidth: 150 }}
          >
            <Stage stage={stage} colors={ICCC_COLORS} />
            {i < stages.length - 1 && (
              <Icon
                sx={{
                  color: ICCC_COLORS.textDim,
                  flexShrink: 0,
                  display: { xs: "none", lg: "block" },
                }}
              >
                chevron_right
              </Icon>
            )}
          </MDBox>
        ))}
      </MDBox>
    </MDBox>
  );
}

InformationFlow.propTypes = {
  rollup: PropTypes.shape({
    total: PropTypes.number,
    critical: PropTypes.number,
    warning: PropTypes.number,
  }),
};
